import React, { useEffect, useRef, useState } from 'react';
import ConfirmModal from '../../ConfirmModal';
import { errorMessage, toastError, toastSuccess } from '../../../lib/toast';
import ItAppForm from './ItAppForm';
import ItAppCatalogField from './ItAppCatalogField';
import { LABELS, MSG, adminConsoleHref, blankApp, serverFieldErrors, toPayload, validateForm } from './itAppRules';
import { createItApp, restoreItApp, retireItApp, updateItApp } from './itAppsApi';

// Seed the editable form from a stored app: nulls become '' for inputs, the
// steps list always offers one line to type into.
function formOf(app) {
  if (!app) return blankApp();
  const blank = blankApp();
  const form = { ...blank };
  Object.keys(blank).forEach((k) => {
    if (app[k] !== null && app[k] !== undefined) form[k] = app[k];
  });
  if (!form.offboarding_steps.length) form.offboarding_steps = [''];
  return form;
}

function ReadOnly({ app }) {
  // The server sends a vetted https link (or null); derive only if an older row lacks it.
  const href = 'admin_console_href' in app ? app.admin_console_href : adminConsoleHref(app.admin_console);
  const label = (field) => LABELS[field][app[field]] || app[field];
  const rows = [
    ['Company', label('company')],
    ['IT role', label('it_role')],
    ['Tool owner', app.tool_owner],
    ['IT owner', app.it_owner],
    ['Admin console', href
      ? <a href={href} target="_blank" rel="noopener noreferrer">{app.admin_console}</a>
      : app.admin_console],
    ['Sign-in', label('sign_in')],
    ['Billing', label('billing')],
    ['Billed by', app.billing === 'no_cost' ? 'Not billed' : label('billed_by')],
    ...(app.billing !== 'no_cost' && app.billed_by === 'partner' ? [['Partner name', app.partner_name]] : []),
    ['Close action', app.close_action],
    ['Seat note', app.seat_note],
    ['Notes', app.notes],
  ];
  const list = (items) => (items && items.length
    ? <ol className="ita-ro-list">{items.map((s, i) => <li key={`${i}-${s}`}>{s}</li>)}</ol>
    : <span className="ita-muted">None</span>);
  return (
    <dl className="ita-ro">
      {rows.map(([k, val]) => (
        <div key={k} className="ita-ro-row">
          <dt>{k}</dt>
          <dd>{val || <span className="ita-muted">None</span>}</dd>
        </div>
      ))}
      <div className="ita-ro-row"><dt>Key</dt><dd><code className="ita-key">{app.key}</code></dd></div>
      <div className="ita-ro-row ita-ro-row--block"><dt>Offboarding steps</dt><dd>{list(app.offboarding_steps)}</dd></div>
      <div className="ita-ro-row ita-ro-row--block"><dt>What it covers</dt><dd>{list(app.what_it_covers)}</dd></div>
    </dl>
  );
}

/**
 * Slide-in drawer for one IT app (existing `ut-drawer` pattern). `app` null
 * means "Add app". Editable only with it_apps.manage; the server enforces it too.
 * Calls `onSaved(app, { close })` with the server's copy after every write.
 */
export default function ItAppDrawer({ app, canManage, onClose, onSaved }) {
  const isCreate = !app;
  const [form, setForm] = useState(() => formOf(app));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirmRetire, setConfirmRetire] = useState(false);
  // The linked catalog tool as shown; form.tool_id is what Save sends.
  const [catalogTool, setCatalogTool] = useState(() => app?.tool || null);
  const [shown, setShown] = useState(false);
  const closeRef = useRef(null);
  const lastFocus = useRef(null);
  const title = isCreate ? 'Add app' : app.name;
  const saveName = form.name.trim() || app?.name || 'this app';

  // Slide in after mount, focus the close button, give focus back on close.
  useEffect(() => {
    lastFocus.current = document.activeElement;
    closeRef.current?.focus();
    const raf = window.requestAnimationFrame(() => setShown(true));
    return () => {
      window.cancelAnimationFrame(raf);
      const el = lastFocus.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      // The confirmation owns Escape while it is open.
      if (e.key === 'Escape' && !confirmRetire) {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, confirmRetire]);

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    const local = validateForm(form, { isCreate });
    setErrors(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      const body = toPayload(form, { isCreate });
      const saved = isCreate ? await createItApp(body) : await updateItApp(app.id, body);
      toastSuccess(`Saved ${saved?.name || body.name}.`);
      onSaved(saved, { close: true });
    } catch (err) {
      const data = err?.response?.data;
      const dup = errorMessage(err, '', { duplicate_key: MSG.duplicate_key });
      if (dup === MSG.duplicate_key) setErrors({ key: dup });
      else if (data?.error === 'validation' && data.fields) setErrors(serverFieldErrors(data.fields));
      else toastError(`Couldn't save ${saveName}. Try again.`);
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (active) => {
    setConfirmRetire(false);
    if (busy) return;
    setBusy(true);
    try {
      const saved = active ? await restoreItApp(app.id) : await retireItApp(app.id);
      toastSuccess(active
        ? `${app.name} is active again.`
        : `${app.name} retired. It no longer shows in offboarding.`);
      onSaved(saved, { close: false });
    } catch {
      toastError(`Couldn't save ${app.name}. Try again.`);
    } finally {
      setBusy(false);
    }
  };

  const selectTool = (tool) => {
    setCatalogTool(tool);
    setForm((f) => ({ ...f, tool_id: tool ? tool.id : null }));
  };
  // "Add to catalog" already saved the link server-side: mirror it and refresh the list.
  const catalogCreated = (saved) => {
    if (!saved) return;
    setCatalogTool(saved.tool || null);
    setForm((f) => ({ ...f, tool_id: saved.tool_id ?? null }));
    onSaved(saved, { close: false });
  };

  const retired = app?.active === false;

  return (
    <>
      <div className={`ut-drawer-backdrop${shown ? ' is-open' : ''}`} aria-hidden="true" onClick={onClose} />
      <aside className={`ut-drawer ita-drawer${shown ? ' is-open' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="ita-drawer-head">
          <button ref={closeRef} type="button" className="ita-icon-btn ita-close" onClick={onClose} aria-label="Close">×</button>
          <div className="ita-drawer-titles">
            <p className="ita-drawer-title">{title}</p>
            {app && (
              <p className="ita-drawer-sub">
                {LABELS.company[app.company]} · {LABELS.it_role[app.it_role]}
                {retired && <span className="ita-chip">Retired</span>}
              </p>
            )}
          </div>
        </header>
        <div className="ut-drawer-body ita-drawer-body">
          {canManage ? (
            <form id="ita-form" onSubmit={save} noValidate>
              <ItAppForm form={form} errors={errors} isCreate={isCreate} onChange={setForm} />
            </form>
          ) : <ReadOnly app={app} />}
          {/* Outside the <form>: its own inputs and buttons must never submit the drawer. */}
          <ItAppCatalogField
            appId={app?.id ?? null}
            appName={form.name.trim() || app?.name || ''}
            tool={canManage ? catalogTool : app?.tool || null}
            canManage={canManage}
            onSelect={selectTool}
            onCreated={catalogCreated}
          />
        </div>
        {canManage && (
          <footer className="ita-drawer-foot">
            {!isCreate && (retired
              ? <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setActive(true)}>Restore</button>
              : <button type="button" className="btn btn-danger-outline" disabled={busy} onClick={() => setConfirmRetire(true)}>Retire</button>)}
            <span className="ita-foot-spacer" />
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" form="ita-form" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </footer>
        )}
      </aside>
      <ConfirmModal
        isOpen={confirmRetire}
        title={`Retire ${app?.name || ''}?`}
        message="It stops showing in offboarding. Its history stays, and you can restore it later."
        confirmText="Retire app"
        isDangerous
        onConfirm={() => setActive(false)}
        onCancel={() => setConfirmRetire(false)}
      />
    </>
  );
}
